import React, { useState } from 'react';
import {
  Modal, TextInput, Button, Group, Stack, Text, Paper, Badge, Progress, Box,
  Divider, LoadingOverlay, SegmentedControl
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconSearch, IconWifi, IconTicket, IconDownload, IconClock, IconRefresh,
  IconCheck, IconAlertCircle, IconX, IconUser, IconPhone
} from '@tabler/icons-react';

// External Captive Portal API URL
const EXTERNAL_API_URL = 'http://192.168.1.7/api/captive';
const CSRF_TOKEN = 'EKuyzIWuv2v50fNYvlFI6AMp5R2WujPmdlWZlUKAgH8dlEemRAr4wWGnCjQrbJ93';

/**
 * VoucherSearchModal - Modal for searching and viewing voucher details
 * @param {boolean} opened - Whether the modal is open
 * @param {function} onClose - Function to close the modal
 */
export default function VoucherSearchModal({ opened, onClose }) {
  const [searchType, setSearchType] = useState('email'); // 'email', 'code', 'mac'
  const [searchValue, setSearchValue] = useState('');
  const [voucherData, setVoucherData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Reset state when modal closes
  const handleClose = () => {
    setSearchValue('');
    setVoucherData(null);
    setError(null);
    setHasSearched(false);
    onClose();
  };

  // Search for voucher
  const handleSearch = async () => {
    if (!searchValue.trim()) {
      notifications.show({
        title: 'Search Required',
        message: `Please enter a ${searchType} to search`,
        color: 'orange',
        autoClose: 3000
      });
      return;
    }

    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      // Search using the external captive portal API
      const response = await fetch(`${EXTERNAL_API_URL}/vouchers/?search=${encodeURIComponent(searchValue)}`, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'X-CSRFTOKEN': CSRF_TOKEN
        }
      });

      const data = await response.json();
      setLoading(false);

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch voucher data');
      }

      // Handle array response - find matching voucher
      const vouchers = Array.isArray(data) ? data : data.results || [];
      
      if (vouchers.length > 0) {
        // Get the first matching voucher
        const voucher = vouchers[0];
        
        // Transform external API data to internal format
        const transformedData = {
          // Map external fields to internal format
          voucherCode: voucher.code,
          custName: voucher.cust_name,
          custNumber: voucher.cust_number,
          projectId: voucher.project_id,
          price: voucher.price,
          period: voucher.period,
          dataLimitMB: voucher.data_limit_mb,
          status: voucher.status,
          isFree: voucher.is_free,
          activatedAt: voucher.activated_at,
          expiryTime: voucher.expiry_time,
          availabilityFrom: voucher.availability_from,
          availabilityTo: voucher.availability_to,
          smsSent: voucher.sms_sent,
          tokens: voucher.tokens,
          partnerId: voucher.partner_id,
          createdAt: voucher.created_at,
          id: voucher.id,
          // Calculated fields
          isExpired: voucher.status === 'expired' || (voucher.expiry_time && new Date(voucher.expiry_time) < new Date()),
          timeRemainingMs: voucher.expiry_time ? Math.max(0, new Date(voucher.expiry_time) - new Date()) : 0,
          timeRemainingMinutes: voucher.expiry_time ? Math.round((new Date(voucher.expiry_time) - new Date()) / 60000) : 0,
          timeRemainingFormatted: voucher.expiry_time ? (() => {
            const mins = Math.max(0, Math.round((new Date(voucher.expiry_time) - new Date()) / 60000));
            return `${Math.floor(mins / 60)}h ${mins % 60}m`;
          })() : 'N/A',
          dataUsedMB: 0,
          dataPercentUsed: 0
        };
        
        setVoucherData(transformedData);
        notifications.show({
          title: 'Voucher Found',
          message: `Voucher ${voucher.code} retrieved successfully`,
          color: 'violet',
          autoClose: 3000,
          icon: <IconCheck size={16} />
        });
      } else {
        throw new Error('No voucher found');
      }
    } catch (err) {
      setLoading(false);
      setError(err.message);
      notifications.show({
        title: 'Voucher Not Found',
        message: err.message,
        color: 'orange',
        autoClose: 3000
      });
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Calculate data usage percentage
  const dataPercentage = voucherData?.dataPercentUsed || 0;
  
  // Format MAC address for display
  const formatMacAddress = (mac) => {
    if (!mac || mac === 'Not bound') return 'Not bound';
    return mac.toUpperCase();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <IconWifi size={20} color="#9381ff" />
          <Text fw={600} c="#2d2d2d">Voucher Search</Text>
        </Group>
      }
      size="md"
      centered
      closeButtonProps={{ icon: <IconX size={18} /> }}
    >
      <LoadingOverlay visible={loading} overlayProps={{ blur: 2 }} />
      
      <Stack gap="md">
        {/* Search Type Selector */}
        <SegmentedControl
          value={searchType}
          onChange={setSearchType}
          data={[
            { label: 'Code', value: 'code' },
            { label: 'Customer Name', value: 'name' },
            { label: 'Phone', value: 'phone' }
          ]}
          fullWidth
        />

        {/* Search Input */}
        <Group gap="sm">
          <TextInput
            placeholder={searchType === 'code' ? 'Enter voucher code' : 
                         searchType === 'name' ? 'Enter customer name' : 
                         'Enter phone number'}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyPress={handleKeyPress}
            style={{ flex: 1 }}
            leftSection={<IconSearch size={16} />}
          />
          <Button 
            onClick={handleSearch}
            loading={loading}
            variant="filled"
            color="violet"
          >
            Search
          </Button>
        </Group>

        <Divider />

        {/* Error Message */}
        {error && !voucherData && (
          <Stack align="center" gap="xs" py="md">
            <IconAlertCircle size={32} color="#fa5252" />
            <Text c="red" size="sm" ta="center">
              {error}
            </Text>
          </Stack>
        )}

        {/* Voucher Data Display */}
        {!loading && voucherData && (
          <Stack gap="md">
            {/* Voucher Code */}
            <Box>
              <Group gap="xs" mb={4}>
                <IconTicket size={16} color="#9381ff" />
                <Text size="xs" c="dimmed">Voucher Code</Text>
              </Group>
              <Badge 
                size="xl" 
                variant="light" 
                color="violet"
                style={{ textTransform: 'uppercase' }}
              >
                {voucherData.voucherCode || 'N/A'}
              </Badge>
            </Box>

            <Divider color="#e8e5f0" />

            {/* Customer Name */}
            <Box>
              <Group gap="xs" mb={4}>
                <IconUser size={16} color="#228be6" />
                <Text size="xs" c="dimmed">Customer Name</Text>
              </Group>
              <Text c="#2d2d2d" fw={600} size="sm">
                {voucherData.custName || 'N/A'}
              </Text>
            </Box>

            <Divider color="#e8e5f0" />

            {/* Phone Number */}
            <Box>
              <Group gap="xs" mb={4}>
                <IconPhone size={16} color="#228be6" />
                <Text size="xs" c="dimmed">Phone Number</Text>
              </Group>
              <Text c="#2d2d2d" fw={500} size="sm">
                {voucherData.custNumber || 'N/A'}
              </Text>
            </Box>

            <Divider color="#e8e5f0" />

            {/* Period / Duration */}
            <Box>
              <Group gap="xs" mb={4}>
                <IconClock size={16} color="#be4bdb" />
                <Text size="xs" c="dimmed">Duration</Text>
              </Group>
              <Text c="#2d2d2d" fw={600} size="lg">
                {voucherData.period || 0} minutes
              </Text>
            </Box>

            <Divider color="#e8e5f0" />

            {/* Expiry Time */}
            <Box>
              <Group gap="xs" mb={4}>
                <IconClock size={16} color={voucherData.isExpired ? '#fa5252' : '#be4bdb'} />
                <Text size="xs" c="dimmed">Expiry Time</Text>
              </Group>
              <Text 
                c={voucherData.isExpired ? 'red' : '#2d2d2d'}
                fw={600} 
                size="sm"
              >
                {voucherData.expiryTime ? new Date(voucherData.expiryTime).toLocaleString() : 'N/A'}
              </Text>
              <Text c={voucherData.isExpired ? 'red' : 'dimmed'} size="xs" mt={4}>
                {voucherData.timeRemainingFormatted || 'Expired'}
              </Text>
            </Box>

            <Divider color="#e8e5f0" />

            {/* Status Badge */}
            <Box>
              <Group justify="center">
                <Badge 
                  size="lg" 
                  variant="dot"
                  color={voucherData.status === 'active' ? 'green' : voucherData.status === 'expired' ? 'red' : 'gray'}
                >
                  {voucherData.status?.toUpperCase() || 'UNKNOWN'}
                </Badge>
              </Group>
            </Box>

            {/* Additional Info */}
            <Box>
              <Divider color="#e8e5f0" mb="md" />
              <Group gap="lg">
                <Box>
                  <Text size="xs" c="dimmed">Project ID</Text>
                  <Text size="sm" fw={500}>{voucherData.projectId || 'N/A'}</Text>
                </Box>
                <Box>
                  <Text size="xs" c="dimmed">Price</Text>
                  <Text size="sm" fw={500}>{voucherData.price || '0.00'}</Text>
                </Box>
                <Box>
                  <Text size="xs" c="dimmed">Free</Text>
                  <Text size="sm" fw={500}>{voucherData.isFree ? 'Yes' : 'No'}</Text>
                </Box>
              </Group>
              {voucherData.activatedAt && (
                <>
                  <Text size="xs" c="dimmed" mt="sm">
                    Activated: {new Date(voucherData.activatedAt).toLocaleString()}
                  </Text>
                  <Text size="xs" c="dimmed">
                    Created: {voucherData.createdAt ? new Date(voucherData.createdAt).toLocaleString() : 'N/A'}
                  </Text>
                </>
              )}
            </Box>
          </Stack>
        )}

        {/* Empty State */}
        {!loading && !voucherData && !error && hasSearched && (
          <Text c="dimmed" size="sm" ta="center" py="md">
            No voucher found. Try a different search term.
          </Text>
        )}

        {/* Initial State */}
        {!loading && !voucherData && !error && !hasSearched && (
          <Text c="dimmed" size="sm" ta="center" py="md">
            Search by email, voucher code, or MAC address to view voucher details.
          </Text>
        )}
      </Stack>
    </Modal>
  );
}